import {Injectable} from '@angular/core';
import {Observable, from, forkJoin, firstValueFrom} from 'rxjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {AdxInvoiceGridService} from '../../adx-invoice/service/adx-invoice-grid.service';
import {AdxPaymentGridService} from '../../adx-payment/service/adx-payment-grid.service';
import {AdxBookingToTramsInvoiceLinkGridService} from '../../adx-booking-to-trams-invoice-link/service/adx-booking-to-trams-invoice-link-grid.service';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TripPdfService {
  constructor(
    private adxInvoiceGridService: AdxInvoiceGridService,
    private adxPaymentGridService: AdxPaymentGridService,
    private adxBookingToTramsInvoiceLinkGridService: AdxBookingToTramsInvoiceLinkGridService
  ) {}

  generatePdfFromHtml(tripId: number): Observable<void> {
    return from(this.generateAndDownloadPdf(tripId));
  }

  private async generateAndDownloadPdf(tripId: number): Promise<void> {
    const tripDetailsElement = document.querySelector('trip-details');
    if (!tripDetailsElement) {
      throw new Error('Trip details element not found');
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    const allGridData = await firstValueFrom(this.fetchAllGridData(tripId));

    const page1Container = document.createElement('div');
    page1Container.style.width = '300mm';
    page1Container.style.maxWidth = '300mm';
    page1Container.style.padding = '8mm 12mm';
    page1Container.style.margin = '0';
    page1Container.style.fontFamily = 'Arial, Helvetica, sans-serif';
    page1Container.style.fontSize = '11px';
    page1Container.style.lineHeight = '1.4';
    page1Container.style.color = '#333';
    page1Container.style.backgroundColor = 'white';
    page1Container.style.position = 'absolute';
    page1Container.style.top = '-9999px';
    page1Container.style.left = '-9999px';
    page1Container.style.boxSizing = 'border-box';
    page1Container.style.height = '200mm';
    page1Container.style.overflow = 'hidden';

    const pdfContainers = this.createPaginatedContent(page1Container, tripDetailsElement, allGridData);

    pdfContainers.forEach((container: HTMLElement) => {
      document.body.appendChild(container);
    });

    try {
      const pdf = new jsPDF('l', 'mm', 'a4');
      const imgWidth = 297;
      const pageHeight = 210;

      const renderPromises = pdfContainers.map(async (container, index) => {
        container.style.top = '0px';
        container.style.left = '0px';

        await new Promise(resolve => setTimeout(resolve, 100 + (index * 50)));

        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 1130,
          windowWidth: 1130,
          scrollX: 0,
          scrollY: 0,
          logging: false,
          foreignObjectRendering: true
        } as any);

        container.style.top = '-9999px';
        container.style.left = '-9999px';

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        return {
          index,
          imgData,
          imgHeight
        };
      });

      const renderedPages = await Promise.all(renderPromises);
      renderedPages.sort((a, b) => a.index - b.index);
      renderedPages.forEach((page, i) => {
        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(page.imgData, 'JPEG', 0, 0, imgWidth, Math.min(page.imgHeight, pageHeight));
      });

      const timestamp = new Date().toISOString().split('T')[0];
      const fileName = `Trip_Details_${tripId}_${timestamp}.pdf`;
      pdf.save(fileName);
    } finally {
      pdfContainers.forEach((container: HTMLElement) => {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      });
    }
  }

  private fetchAllGridData(tripId: number) {
    const filters: any = { tripId: tripId };

    const getAllInvoices = () => {
      return this.adxInvoiceGridService.getItems({
        filters,
        page: { pageSize: 10000, pageIndex: 0 },
        sort: { active: 'id', direction: 'asc' }
      }).pipe(map(response => response.items));
    };

    const getAllPayments = () => {
      return this.adxPaymentGridService.getItems({
        filters,
        page: { pageSize: 10000, pageIndex: 0 },
        sort: { active: 'dateUpdated', direction: 'desc' }
      }).pipe(map(response => response.items));
    };

    const getAllTramsInvoiceLinks = () => {
      return this.adxBookingToTramsInvoiceLinkGridService.getItems({
        filters,
        page: { pageSize: 10000, pageIndex: 0 },
        sort: { active: 'id', direction: 'asc' }
      }).pipe(map(response => response.items));
    };

    return forkJoin({
      invoices: getAllInvoices(),
      payments: getAllPayments(),
      tramsInvoiceLinks: getAllTramsInvoiceLinks()
    });
  }

  private createPaginatedContent(page1Container: HTMLElement, originalElement: Element, allGridData?: any): HTMLElement[] {
    const containers: HTMLElement[] = [page1Container];

    page1Container.setAttribute('data-pdf-container', 'page1');

    const tripHeader = this.extractTripHeader(originalElement);
    if (tripHeader) {
      page1Container.appendChild(tripHeader);
    }

    const detailsSections = this.extractDetailsSections(originalElement);
    detailsSections.forEach((section) => {
      page1Container.appendChild(section);
    });

    const tables = this.extractTables(originalElement, allGridData);

    if (tables.length === 0) {
      return containers;
    }

    const tableContainers = this.paginateTables(tables);
    containers.push(...tableContainers);

    return containers;
  }

  private paginateTables(tables: HTMLElement[]): HTMLElement[] {
    const tableContainers: HTMLElement[] = [];
    const maxRowsPerPage = 15;

    tables.forEach((tableContainer) => {
      const table = tableContainer.querySelector('table');
      if (!table) {
        const container = this.createPageContainer();
        container.appendChild(tableContainer);
        tableContainers.push(container);
        return;
      }

      const tableHeader = tableContainer.querySelector('h2');
      const headerRow = table.querySelector('tr:first-child');
      const dataRows = Array.from(table.querySelectorAll('tr')).slice(1);

      if (dataRows.length <= maxRowsPerPage) {
        const container = this.createPageContainer();
        container.appendChild(tableContainer);
        tableContainers.push(container);
      } else {
        let currentPage = 0;
        for (let i = 0; i < dataRows.length; i += maxRowsPerPage) {
          const container = this.createPageContainer();

          const pageTableContainer = document.createElement('div');
          pageTableContainer.style.cssText = tableContainer.style.cssText;

          if (currentPage === 0 && tableHeader) {
            const clonedHeader = tableHeader.cloneNode(true) as HTMLElement;
            pageTableContainer.appendChild(clonedHeader);
          } else if (currentPage > 0 && tableHeader) {
            const continuationHeader = tableHeader.cloneNode(true) as HTMLElement;
            continuationHeader.textContent += ` (continued)`;
            pageTableContainer.appendChild(continuationHeader);
          }

          const pageTable = document.createElement('table');
          pageTable.style.cssText = table.style.cssText;

          if (headerRow) {
            const clonedHeaderRow = headerRow.cloneNode(true) as HTMLElement;
            pageTable.appendChild(clonedHeaderRow);
          }

          const pageRows = dataRows.slice(i, i + maxRowsPerPage);
          pageRows.forEach((row, index) => {
            const clonedRow = row.cloneNode(true) as HTMLElement;
            if ((i + index) % 2 === 1) {
              clonedRow.style.backgroundColor = '#f8f9fa';
            }
            pageTable.appendChild(clonedRow);
          });

          pageTableContainer.appendChild(pageTable);
          container.appendChild(pageTableContainer);
          tableContainers.push(container);
          currentPage++;
        }
      }
    });

    return tableContainers;
  }

  private createPageContainer(): HTMLElement {
    const container = document.createElement('div');
    container.style.width = '300mm';
    container.style.maxWidth = '300mm';
    container.style.padding = '8mm 12mm';
    container.style.margin = '0';
    container.style.fontFamily = 'Arial, Helvetica, sans-serif';
    container.style.fontSize = '11px';
    container.style.lineHeight = '1.4';
    container.style.color = '#333';
    container.style.backgroundColor = 'white';
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.boxSizing = 'border-box';
    container.style.height = 'auto';
    container.style.minHeight = '200mm';
    container.style.overflow = 'visible';

    return container;
  }

  private extractTripHeader(originalElement: Element): HTMLElement | null {
    const headerElement = originalElement.querySelector('.trip-header, h1, h2, .page-details-title-style');

    const header = document.createElement('div');
    header.style.cssText = `
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 3px solid rgb(0, 89, 63);
      width: 100%;
    `;

    const title = document.createElement('h1');
    title.style.cssText = `
      font-size: 20px;
      font-weight: bold;
      color: rgb(0, 89, 63);
      margin: 0;
      padding: 0;
    `;

    if (headerElement) {
      const headerText = headerElement.textContent?.trim() || 'Trip Details';
      const cleanTitle = headerText.replace(/\s*Generating PDF\.\.\.\s*/gi, '').trim();
      title.textContent = cleanTitle || 'Trip Details';
    } else {
      title.textContent = 'Trip Details';
    }

    header.appendChild(title);
    return header;
  }

  private extractDetailsSections(originalElement: Element): HTMLElement[] {
    const sections: HTMLElement[] = [];

    const readOnlyFields = originalElement.querySelectorAll('read-only-form-field');

    const tripFields = ['Trip Name', 'Trip Total', 'Adx Quote Number', 'Trip Record Locator', 'Trip Start Date', 'Trip End Date'];
    const paymentFields = ['Open Payments Received From Client', 'Closed Payments Received From Client', 'Open Payments To Vendor', 'Closed Payments To Vendor'];

    const extractedFields = new Map<string, string>();
    const processedValues = new Set<string>();

    readOnlyFields.forEach((element) => {
      const fieldData = this.extractFieldData(element);
      if (fieldData && fieldData.value !== 'N/A' && fieldData.value.trim() && fieldData.value.length > 2) {
        const uniqueKey = `${fieldData.label}:${fieldData.value}`;
        if (!processedValues.has(uniqueKey)) {
          extractedFields.set(fieldData.label, fieldData.value);
          processedValues.add(uniqueKey);
        }
      }
    });

    const tripInfoFields: Array<{ label: string; value: string }> = [];
    tripFields.forEach(fieldName => {
      if (extractedFields.has(fieldName)) {
        tripInfoFields.push({ label: fieldName, value: extractedFields.get(fieldName)! });
      }
    });

    if (tripInfoFields.length > 0) {
      const tripSection = this.createFieldSection('Trip Information', tripInfoFields, 2);
      sections.push(...tripSection);
    }

    const paymentInfoFields: Array<{ label: string; value: string }> = [];
    paymentFields.forEach(fieldName => {
      if (extractedFields.has(fieldName)) {
        paymentInfoFields.push({ label: fieldName, value: extractedFields.get(fieldName)! });
      }
    });

    if (paymentInfoFields.length > 0) {
      const paymentSection = this.createFieldSection('Payment Totals', paymentInfoFields, 2);
      sections.push(...paymentSection);
    }

    if (sections.length === 0) {
      const fallbackSection = this.createFallbackContent(originalElement);
      if (fallbackSection) {
        sections.push(fallbackSection);
      }
    }

    return sections;
  }

  private createFieldSection(sectionTitle: string, fields: Array<{ label: string; value: string }>, columns: number = 4): HTMLElement[] {
    const sectionElements: HTMLElement[] = [];

    if (fields.length === 0) return sectionElements;

    const sectionHeader = document.createElement('h2');
    sectionHeader.textContent = sectionTitle;
    sectionHeader.style.cssText = `
      font-size: 16px;
      font-weight: bold;
      color: rgb(0, 89, 63);
      margin: 20px 0 10px 0;
      padding-bottom: 5px;
      border-bottom: 2px solid rgb(0, 89, 63);
    `;
    sectionElements.push(sectionHeader);

    const fieldsGrid = document.createElement('div');
    fieldsGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${columns}, 1fr);
      gap: 12px;
      margin-bottom: 20px;
    `;

    fields.forEach(fieldData => {
      const fieldElement = this.createFieldElement(fieldData.label, fieldData.value);
      fieldsGrid.appendChild(fieldElement);
    });

    sectionElements.push(fieldsGrid);

    return sectionElements;
  }

  private createFallbackContent(originalElement: Element): HTMLElement | null {
    const section = document.createElement('div');
    section.style.cssText = `
      margin-bottom: 20px;
      padding: 20px;
      border: 1px solid #ddd;
      border-radius: 8px;
      background-color: #f8f9fa;
    `;

    const title = document.createElement('h2');
    title.textContent = 'Trip Details';
    title.style.cssText = `
      font-size: 16px;
      font-weight: bold;
      color: rgb(0, 89, 63);
      margin: 0 0 15px 0;
    `;
    section.appendChild(title);

    const content = document.createElement('div');
    content.style.cssText = `
      font-size: 12px;
      line-height: 1.4;
      color: #333;
    `;

    const textContent = originalElement.textContent?.trim() || 'No trip information available';
    const lines = textContent.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .slice(0, 10);

    lines.forEach(line => {
      const lineDiv = document.createElement('div');
      lineDiv.style.marginBottom = '5px';
      lineDiv.textContent = line;
      content.appendChild(lineDiv);
    });

    section.appendChild(content);
    return section;
  }

  private extractFieldData(element: Element): { label: string; value: string } | null {
    let label = '';
    let value = '';

    if (element.tagName.toLowerCase() === 'read-only-form-field') {
      label = element.getAttribute('title') || element.getAttribute('ng-reflect-title') || '';
      value = element.getAttribute('ng-reflect-value') || '';

      if (!value) {
        const valueEl = element.querySelector('.form-field-value, .field-value, .value, input');
        if (valueEl) value = (valueEl as HTMLInputElement).value || valueEl.textContent?.trim() || '';
      }

      if (label && value) {
        return { label, value };
      }
    }

    return null;
  }

  private createFieldElement(label: string, value: string): HTMLElement {
    const fieldDiv = document.createElement('div');
    fieldDiv.style.cssText = `
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 12px 10px;
      background-color: #f8f9fa;
      min-height: 45px;
      display: flex;
      flex-direction: column;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    `;

    const labelDiv = document.createElement('div');
    labelDiv.style.cssText = `
      font-size: 9px;
      font-weight: bold;
      color: #666;
      margin-bottom: 6px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    `;
    labelDiv.textContent = label;

    const valueDiv = document.createElement('div');
    valueDiv.style.cssText = `
      font-size: 11px;
      color: #222;
      word-wrap: break-word;
      flex: 1;
      font-weight: 500;
    `;
    valueDiv.textContent = value;

    fieldDiv.appendChild(labelDiv);
    fieldDiv.appendChild(valueDiv);

    return fieldDiv;
  }

  private extractTables(originalElement: Element, allGridData?: any): HTMLElement[] {
    const tables: HTMLElement[] = [];

    if (!allGridData) {
      return this.extractTablesFromDOM(originalElement);
    }

    const tableConfigs = [
      { title: 'Invoices', data: allGridData.invoices, columns: this.getInvoiceColumns() },
      { title: 'Payments', data: allGridData.payments, columns: this.getPaymentColumns() },
      { title: 'Trams Invoice Links', data: allGridData.tramsInvoiceLinks, columns: this.getTramsInvoiceLinkColumns() }
    ];

    tableConfigs.forEach((config) => {
      if (config.data && config.data.length > 0) {
        const tableContainer = this.createTableFromData(config.title, config.data, config.columns);
        tables.push(tableContainer);
      }
    });

    return tables;
  }

  private extractTablesFromDOM(originalElement: Element): HTMLElement[] {
    const tables: HTMLElement[] = [];
    const matTables = originalElement.querySelectorAll('mat-table, .mat-table, table');

    const tableNames = ['Invoices', 'Payments', 'Trams Invoice Links'];

    matTables.forEach((table, index) => {
      const tableTitle = tableNames[index] || `Data Table ${index + 1}`;

      const tableContainer = document.createElement('div');
      tableContainer.style.cssText = `
        margin-bottom: 30px;
        page-break-inside: avoid;
      `;

      const tableHeader = document.createElement('h2');
      tableHeader.textContent = tableTitle;
      tableHeader.style.cssText = `
        font-size: 16px;
        font-weight: bold;
        color: rgb(0, 89, 63);
        margin: 25px 0 15px 0;
        padding-bottom: 8px;
        border-bottom: 2px solid rgb(0, 89, 63);
      `;

      const pdfTable = document.createElement('table');
      pdfTable.style.cssText = `
        width: 100%;
        border-collapse: collapse;
        font-size: 10px;
        background: white;
        overflow: visible;
        margin-top: 10px;
        table-layout: fixed;
      `;

      let headerRow = table.querySelector('mat-header-row, .mat-header-row, thead tr');

      if (!headerRow) {
        const allRows = table.querySelectorAll('tr, mat-row, .mat-row');
        headerRow = allRows[0];
      }

      if (headerRow) {
        const pdfHeaderRow = document.createElement('tr');
        const headers = headerRow.querySelectorAll('mat-header-cell, th, .mat-header-cell, .mat-column-header, [role="columnheader"], .cdk-column-header, [class*="mat-column-"]');

        const headerElements = headers.length > 0 ? headers : headerRow.children;

        const allHeaders: Array<{text: string, index: number, element: Element}> = [];

        Array.from(headerElements).forEach((header, index) => {
          const headerText = header.textContent?.trim() || '';
          allHeaders.push({text: headerText, index, element: header});
        });

        allHeaders.forEach(({text, index}) => {
          if (text.toLowerCase() === 'actions' ||
              text.toLowerCase() === 'action') {
            return;
          }

          const th = document.createElement('th');
          th.style.cssText = `
            background-color: rgb(0, 89, 63);
            color: white;
            padding: 8px 6px;
            text-align: left;
            font-weight: bold;
            border: 1px solid #ddd;
            font-size: 10px;
            white-space: normal;
            word-wrap: break-word;
            line-height: 1.2;
            min-width: 60px;
            max-width: 120px;
            vertical-align: top;
          `;
          const displayText = text || `Col ${index + 1}`;
          th.textContent = displayText;
          th.setAttribute('data-original-index', index.toString());
          pdfHeaderRow.appendChild(th);
        });

        if (pdfHeaderRow.children.length > 0) {
          pdfTable.appendChild(pdfHeaderRow);
        }

        (pdfTable as any).allHeaders = allHeaders;
      }

      const dataRows = table.querySelectorAll('mat-row, .mat-row, tbody tr, tr:not(.mat-header-row):not(thead tr)');
      let rowCount = 0;

      const allHeaders = (pdfTable as any).allHeaders || [];

      dataRows.forEach((row) => {
        const pdfDataRow = document.createElement('tr');
        let cells: Element[] = Array.from(row.querySelectorAll('mat-cell, td, .mat-cell, .mat-column-cell, [role="gridcell"], .cdk-cell, [class*="mat-column-"]'));

        if (cells.length === 0) {
          cells = Array.from(row.children);
        }

        if (cells.length > 0) {
          if (rowCount % 2 === 1) {
            pdfDataRow.style.backgroundColor = '#f8f9fa';
          }

          cells.forEach((cell, cellIndex) => {
            const headerInfo = allHeaders.find((h: any) => h.index === cellIndex);
            const isActionsColumn = headerInfo &&
              (headerInfo.text.toLowerCase() === 'actions' || headerInfo.text.toLowerCase() === 'action');

            if (!isActionsColumn) {
              const td = document.createElement('td');
              td.style.cssText = `
                padding: 6px 4px;
                border: 1px solid #ddd;
                text-align: left;
                font-size: 10px;
                overflow: visible;
                word-wrap: break-word;
                max-width: 120px;
                vertical-align: top;
                line-height: 1.3;
              `;

              let cellText = this.extractCellContent(cell);

              td.textContent = cellText;
              pdfDataRow.appendChild(td);
            }
          });

          if (pdfDataRow.children.length > 0) {
            pdfTable.appendChild(pdfDataRow);
            rowCount++;
          }
        }
      });

      if (pdfTable.children.length > 0) {
        tableContainer.appendChild(tableHeader);
        tableContainer.appendChild(pdfTable);
        tables.push(tableContainer);
      }
    });

    return tables;
  }

  private extractCellContent(cell: Element): string {
    let cellText = cell.textContent?.trim() || '';

    const matIcon = cell.querySelector('mat-icon');
    if (matIcon) {
      const iconName = matIcon.textContent?.trim();

      switch (iconName) {
        case 'done':
          return '✔';
        case 'check':
          return '✔';
        case 'check_circle':
          return '✔';
        case 'checkmark':
          return '✔';
        case 'clear':
          return '✘';
        case 'close':
          return '✘';
        case 'cancel':
          return '✘';
        case 'remove':
          return '—';
        case 'block':
          return '✘';
        default:
          if (iconName && iconName.length > 0) {
            return iconName;
          }
      }
    }

    if (cellText.includes('edit_off') || cellText.includes('history') ||
        cellText.includes('visibility') || cellText.includes('visibility_off')) {
      cellText = '';
    }

    if (cellText === '✓' || cellText === '✔' || cellText.includes('done') ||
        cellText.includes('check') || cellText === 'checkmark') {
      return 'Yes';
    }
    if (cellText === '✗' || cellText === '✘' || cellText === '×' || cellText.includes('clear') ||
        cellText.includes('close') || cellText.includes('cancel')) {
      return 'No';
    }
    if (cellText === '—' || cellText === '-' || cellText === '–' || cellText.includes('remove')) {
      return '—';
    }

    if (!cellText || cellText.length === 0) {
      const checkbox = cell.querySelector('mat-checkbox input[type="checkbox"], input[type="checkbox"]');
      if (checkbox) {
        const isChecked = (checkbox as HTMLInputElement).checked;
        return isChecked ? 'Yes' : 'No';
      }

      const matCheckbox = cell.querySelector('mat-checkbox');
      if (matCheckbox) {
        const ariaChecked = matCheckbox.getAttribute('aria-checked');
        const ngReflectChecked = matCheckbox.getAttribute('ng-reflect-checked');

        if (ariaChecked === 'true' || ngReflectChecked === 'true') return 'Yes';
        if (ariaChecked === 'false' || ngReflectChecked === 'false') return 'No';

        if (matCheckbox.classList.contains('mat-checkbox-checked')) return 'Yes';
        if (matCheckbox.classList.contains('mat-checkbox-unchecked')) return 'No';
      }

      const innerHTML = cell.innerHTML.toLowerCase();
      if (innerHTML.includes('ng-reflect-checked="true"') || innerHTML.includes('checked="true"')) return 'Yes';
      if (innerHTML.includes('ng-reflect-checked="false"') || innerHTML.includes('checked="false"')) return 'No';

      const allElements = cell.querySelectorAll('*');
      for (let i = 0; i < allElements.length; i++) {
        const element = allElements[i];
        const elementText = element.textContent?.trim().toLowerCase();
        if (elementText === 'true' || elementText === 'yes') return 'Yes';
        if (elementText === 'false' || elementText === 'no') return 'No';

        const ngReflectValue = element.getAttribute('ng-reflect-value');
        const ngReflectChecked = element.getAttribute('ng-reflect-checked');
        if (ngReflectValue === 'true' || ngReflectChecked === 'true') return 'Yes';
        if (ngReflectValue === 'false' || ngReflectChecked === 'false') return 'No';
      }

      const select = cell.querySelector('mat-select, select');
      if (select) {
        const selectedValue = (select as HTMLSelectElement).value ||
                             select.textContent?.trim() ||
                             select.getAttribute('ng-reflect-value') ||
                             select.getAttribute('aria-label');
        if (selectedValue && selectedValue !== 'undefined' && selectedValue !== 'null') {
          return selectedValue;
        }
      }

      const input = cell.querySelector('input[type="text"], input[type="number"], input[type="date"]');
      if (input) {
        const inputValue = (input as HTMLInputElement).value;
        if (inputValue) {
          return inputValue;
        }
      }

      const toggle = cell.querySelector('mat-slide-toggle input[type="checkbox"]');
      if (toggle) {
        const isToggled = (toggle as HTMLInputElement).checked;
        return isToggled ? 'Yes' : 'No';
      }

      const anyInput = cell.querySelector('input, textarea');
      if (anyInput) {
        const value = (anyInput as HTMLInputElement).value;
        if (value) {
          return value;
        }
      }

      const ngReflectValue = cell.getAttribute('ng-reflect-value') ||
                            cell.querySelector('[ng-reflect-value]')?.getAttribute('ng-reflect-value');
      if (ngReflectValue && ngReflectValue !== 'undefined' && ngReflectValue !== 'null') {
        if (ngReflectValue === 'true') return 'Yes';
        if (ngReflectValue === 'false') return 'No';
        return ngReflectValue;
      }

      if (cell.classList.contains('true') || cell.querySelector('.true')) return 'Yes';
      if (cell.classList.contains('false') || cell.querySelector('.false')) return 'No';

      const nestedElements = cell.querySelectorAll('span, div, p');
      for (let i = 0; i < nestedElements.length; i++) {
        const element = nestedElements[i];
        const nestedText = element.textContent?.trim();
        if (nestedText && nestedText.length > 0 &&
            !nestedText.includes('edit_off') && !nestedText.includes('history') &&
            !nestedText.includes('visibility')) {
          return nestedText;
        }
      }

      const dataValue = cell.getAttribute('data-value') || cell.getAttribute('data-checked');
      if (dataValue) {
        return dataValue === 'true' ? 'Yes' : dataValue === 'false' ? 'No' : dataValue;
      }
    }

    return cellText;
  }

  private getInvoiceColumns() {
    return [
      { property: 'id', header: 'Invoice Number' },
      { property: 'recordLocator', header: 'Invoice Record Locator' },
      { property: 'branch.name', header: 'Branch Name', accessor: (item: any) => item.branch?.name || '' },
      { property: 'client.profileName', header: 'Client Name', accessor: (item: any) => item.client?.profileName || '' },
      { property: 'invoiceTypeCodeName', header: 'Type' },
      { property: 'invoiceGroup', header: 'Invoice Group' },
      { property: 'issueDate', header: 'Issue Date', accessor: (item: any) => item.issueDate ? new Date(item.issueDate).toLocaleDateString('en-US') : '' },
      { property: 'totalAmount', header: 'Total amount', accessor: (item: any) => item.totalAmount != null ? `$${item.totalAmount.toFixed(2)}` : '' }
    ];
  }

  private getPaymentColumns() {
    return [
      { property: 'fmsAdxPaymentDetailId', header: 'Payment Fms Number' },
      { property: 'payeeName', header: 'Payee Name' },
      { property: 'vendorName', header: 'Vendor Name' },
      { property: 'payTypeName', header: 'Pay Type' },
      { property: 'payMethodName', header: 'Pay Method' },
      { property: 'profileTypeName', header: 'Profile Type' },
      { property: 'amount', header: 'Amount' },
      { property: 'ckCcNo', header: 'Payment Reference Number' },
      { property: 'branchName', header: 'Branch' },
      { property: 'bankName', header: 'Bank' },
      { property: 'paymentDate', header: 'Payment Date', accessor: (item: any) => item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('en-US') : '' },
      { property: 'tripExternalReference', header: 'Trip Record Locator' },
      { property: 'adxInvoiceId', header: 'Invoice Number' }
    ];
  }

  private getTramsInvoiceLinkColumns() {
    return [
      { property: 'adxBooking.travelType.travelTypeName', header: 'Travel Type', accessor: (item: any) => item.adxBooking?.travelType?.travelTypeName || '' },
      { property: 'adxBooking.externalTravelServiceType.name', header: 'External Service Type', accessor: (item: any) => item.adxBooking?.externalTravelServiceType?.name || '' },
      { property: 'adxBookingId', header: 'Adx Service Number' },
      { property: 'isFinal', header: 'Final Payment', accessor: (item: any) => item.isFinal ? 'Yes' : 'No' },
      { property: 'invoiceType.invoiceTypeName', header: 'Invoice Type', accessor: (item: any) => item.invoiceType?.invoiceTypeName || '' },
      { property: 'tramsInvoiceNumber', header: 'Trams Invoice Number' },
      { property: 'isVoucherApplied', header: 'Voucher Applied', accessor: (item: any) => item.invoiceTypeId == 4 ? (item.isVoucherApplied ? 'Yes' : 'No') : '—' },
      { property: 'isProcessed', header: 'Processed', accessor: (item: any) => item.isProcessed ? 'Yes' : 'No' },
      { property: 'isInvalid', header: 'Invalid', accessor: (item: any) => item.isInvalid ? 'Yes' : 'No' },
      { property: 'dateUpdated', header: 'Last Updated', accessor: (item: any) => item.dateUpdated ? new Date(item.dateUpdated).toLocaleDateString('en-US') : '' },
      { property: 'isEligible', header: 'Is Eligible', accessor: (item: any) => item.isEligible ? 'Yes' : 'No' }
    ];
  }

  private createTableFromData(title: string, data: any[], columns: any[]): HTMLElement {
    const tableContainer = document.createElement('div');
    tableContainer.style.cssText = `
      margin-bottom: 30px;
      page-break-inside: avoid;
    `;

    const tableHeader = document.createElement('h2');
    tableHeader.textContent = title;
    tableHeader.style.cssText = `
      font-size: 16px;
      font-weight: bold;
      color: rgb(0, 89, 63);
      margin: 25px 0 15px 0;
      padding-bottom: 8px;
      border-bottom: 2px solid rgb(0, 89, 63);
    `;

    const pdfTable = document.createElement('table');
    pdfTable.style.cssText = `
      width: 100%;
      border-collapse: collapse;
      font-size: 10px;
      background: white;
      overflow: visible;
      margin-top: 10px;
      table-layout: fixed;
    `;

    const headerRow = document.createElement('tr');
    columns.forEach(column => {
      const th = document.createElement('th');
      th.style.cssText = `
        background-color: rgb(0, 89, 63);
        color: white;
        padding: 8px 6px;
        text-align: left;
        font-weight: bold;
        border: 1px solid #ddd;
        font-size: 10px;
        white-space: normal;
        word-wrap: break-word;
        line-height: 1.2;
        min-width: 60px;
        max-width: 120px;
        vertical-align: top;
      `;
      th.textContent = column.header;
      headerRow.appendChild(th);
    });
    pdfTable.appendChild(headerRow);

    data.forEach((item, rowIndex) => {
      const dataRow = document.createElement('tr');
      if (rowIndex % 2 === 1) {
        dataRow.style.backgroundColor = '#f8f9fa';
      }

      columns.forEach(column => {
        const td = document.createElement('td');
        td.style.cssText = `
          padding: 6px 4px;
          border: 1px solid #ddd;
          text-align: left;
          font-size: 10px;
          overflow: visible;
          word-wrap: break-word;
          max-width: 120px;
          vertical-align: top;
          line-height: 1.3;
        `;

        let cellValue = '';
        if (column.accessor) {
          cellValue = column.accessor(item);
        } else {
          cellValue = this.getNestedProperty(item, column.property) || '';
        }

        td.textContent = cellValue.toString();
        dataRow.appendChild(td);
      });

      pdfTable.appendChild(dataRow);
    });

    tableContainer.appendChild(tableHeader);
    tableContainer.appendChild(pdfTable);

    return tableContainer;
  }

  private getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current && current[prop], obj);
  }
}
